import { cmdConsole } from '../state/auth-state';
import { getConsoleTarget } from '../state/auth-state';
import { MOCK } from '../state/device';
import { replay } from '../state/replay';
import { replaySourcePings } from '../map/elevation';
import {
  COMMAND_CATALOG,
  apiEnqueueCommand,
  apiGetDeviceCommands,
  formatCommandResult,
  commandsAtCutoff,
} from '../api/command';
import { state } from '../state/app-state';
import { $ } from './dom';
import { escapeHtml } from './dom';
import { relTime } from './format';
import { showToast } from './toast';
import { deviceDisplayName } from './animals';

export function consoleTargetToken(): string | null {
  return getConsoleTarget()?.token ?? MOCK.device?.token ?? null;
}
export function consoleCanIssue(): boolean {
  return getConsoleTarget() ? getConsoleTarget()!.canIssue : true;   // local device sends as today
}

export function stopCommandConsole(){
  if(cmdConsole.timer != null){ clearTimeout(cmdConsole.timer); cmdConsole.timer = null; }
  cmdConsole.token = null;
  cmdConsole.view = null;
}
export function commandConsoleHasPending(): boolean {
  const now = Date.now();
  return (cmdConsole.view?.commands ?? []).some(c => c.acked_at == null && c.expires_at > now);
}
export async function pollCommandConsole(token: string){
  if(cmdConsole.token !== token) return;            // drawer changed under us
  // Clear any pending tick first so a direct call (e.g. after an enqueue) can't
  // leave two loops running.
  if(cmdConsole.timer != null){ clearTimeout(cmdConsole.timer); cmdConsole.timer = null; }
  try {
    cmdConsole.view = await apiGetDeviceCommands(token);
  } catch (err: any) {
    // 404 (claim lapsed) / 401 (signed out): stop quietly; the panel re-renders.
    if(err?.status === 404 || err?.status === 401){ stopCommandConsole(); return; }
    // transient error: keep the last view, retry on the next tick.
  }
  if(cmdConsole.token !== token) return;
  renderCommandControls();
  renderCommandFeed();
  const next = commandConsoleHasPending() ? 5_000 : 30_000;
  cmdConsole.timer = setTimeout(() => void pollCommandConsole(token), next) as any;
}
export function startCommandConsole(token: string){
  if(cmdConsole.token === token && cmdConsole.timer != null) return;   // already polling this device
  stopCommandConsole();
  cmdConsole.token = token;
  void pollCommandConsole(token);
}

// commandsAtCutoff moved to ./api/command
export function renderCommandFeed(){
  const feed = $('cmd-feed');
  if(!feed) return;
  const view = cmdConsole.view;
  if(!view){ feed.innerHTML = `<div class="acct-hint">Loading…</div>`; return; }
  // During replay of THIS device's own trail, drive the feed from the scrub
  // position; at the live edge, show the full recent history newest-first.
  const replaying = replay.ready && replay.cutoffT !== Infinity && replaySourcePings() === MOCK.pings;
  const rows = replaying ? commandsAtCutoff(view.commands, replay.cutoffT) : view.commands;
  if(rows.length === 0){ feed.innerHTML = `<div class="acct-hint">No commands in this window.</div>`; return; }
  const now = Date.now();
  feed.innerHTML = rows.slice().reverse().map(c => {
    const label = escapeHtml(COMMAND_CATALOG.find(x => x.kind === c.kind)?.label ?? c.kind);
    let stateHtml: string;
    if(c.acked_at != null) stateHtml = `<span class="cmd-ok">${escapeHtml(formatCommandResult(c.kind, c.result_data))}</span>`;
    else if(c.expires_at < now) stateHtml = `<span class="cmd-dead">no response</span>`;
    else stateHtml = `<span class="cmd-wait">waiting…</span>`;
    const when = relTime(c.acked_at ?? c.issued_at);
    return `<div class="cmd-row"><span class="cmd-kind">${label}</span>${stateHtml}<span class="cmd-when">${when}</span></div>`;
  }).join('');
}

// Fill the console dropdown from the device's advertised capabilities (once a
// poll resolves them) and enable/disable the send control accordingly.
export function renderCommandControls(){
  const sel = $('cmd-kind-select') as HTMLSelectElement | null;
  const btn = $('cmd-send-btn') as HTMLButtonElement | null;
  const hint = $('cmd-hint');
  if(!sel || !btn) return;
  const caps = cmdConsole.view?.capabilities ?? null;
  const allowed = COMMAND_CATALOG.filter(c => caps && caps[c.tier] === true);
  if(allowed.length === 0){
    sel.innerHTML = '';
    sel.disabled = true;
    btn.disabled = true;
    if(hint) hint.textContent = caps == null
      ? "This device hasn't enabled remote queries yet."
      : 'This device has withdrawn all remote tiers.';
    return;
  }
  // Preserve the current selection across re-renders where possible.
  const prev = sel.value;
  sel.innerHTML = allowed.map(c => `<option value="${c.kind}|${c.tier}">${escapeHtml(c.label)}</option>`).join('');
  if(prev && allowed.some(c => `${c.kind}|${c.tier}` === prev)) sel.value = prev;
  sel.disabled = false;
  btn.disabled = false;
  if(hint) hint.textContent = 'Pick a query or command and send it to this device. Responses appear below and replay on the trail scrubber.';
}

export async function sendConsoleCommand(){
  if(!consoleCanIssue()) return;          // read-only target — no send (defensive)
  const token = consoleTargetToken();
  if(!token) return;
  const sel = $('cmd-kind-select') as HTMLSelectElement | null;
  if(!sel || !sel.value) return;
  const [kind, tier] = sel.value.split('|') as [string, 'query' | 'command'];
  const btn = $('cmd-send-btn') as HTMLButtonElement | null;
  if(btn) btn.disabled = true;
  try {
    await apiEnqueueCommand(token, tier, kind);
    await pollCommandConsole(token);               // immediate refresh (shows "waiting…")
  } catch (err: any) {
    if(err?.status === 403){ showToast('device withdrew this tier'); await pollCommandConsole(token); }
    else if(err?.status === 404) showToast('claim this device first');
    else if(err?.status === 429) showToast('slow down — rate limited');
    else showToast('send failed');
  } finally {
    if(btn) btn.disabled = false;
  }
}

// A teammate's device console (Phase 3), opened from a map pin. Identity + the
// shared remote-console block, reusing the same cmd-* element ids so
// renderCommandControls()/renderCommandFeed()/startCommandConsole() work
// unchanged. Send controls appear ONLY when the caller owns the token (canIssue);
// otherwise the feed is read-only (ADR 0025: participants view, owner sends).
export function renderTeammateConsole(box: HTMLElement, token: string){
  const dev = state.cohort.get(token);
  const name = dev ? deviceDisplayName(token, dev.animals, null, undefined, dev.tag) : escapeHtml(token);
  const seen = dev?.lastT ? relTime(dev.lastT) : '—';
  const canSend = consoleCanIssue();
  const note = canSend
    ? 'You own this device — send queries/commands below.'
    : 'Read-only — only this device’s owner can send commands.';
  const consoleBlock = canSend
    ? `<div class="acct-hint" id="cmd-hint">Loading the device's advertised queries…</div>` +
      `<div class="menu-row"><select id="cmd-kind-select" class="vis-select" disabled></select>` +
      `<button class="menu-action cmd-send" id="cmd-send-btn" disabled><span>Send</span><i class="ph ph-paper-plane-tilt"></i></button></div>` +
      `<div class="cmd-feed" id="cmd-feed"><div class="acct-hint">Loading…</div></div>`
    : `<div class="cmd-feed" id="cmd-feed"><div class="acct-hint">Loading…</div></div>`;
  box.innerHTML =
    `<div class="device-card"><div class="da-name">${name}</div><div class="da-tail">${escapeHtml(token)}</div></div>` +
    `<div class="acct-hint">Teammate device · last seen ${escapeHtml(seen)}. ${note}</div>` +
    `<hr class="drawer-div"/><div class="menu-label">Remote console</div>` +
    consoleBlock;
  if(canSend) $('cmd-send-btn')?.addEventListener('click', sendConsoleCommand);
  startCommandConsole(token);
  renderCommandControls();
  renderCommandFeed();
}
